import React from "react";
import styled from "styled-components";
import FormField, { Control } from "./FormField";
import { brand_orange_deep, dark } from "../constants/styles";

// Password input with a mask/unmask toggle. Always starts masked. The toggle
// is a real <button> so screen readers and keyboard users reach it.
//
// Props: id, label, required, hint, error, value, onChange, placeholder,
//        autoComplete, onBlur
//
// Use inside a form like:
//   <PasswordField id="password" label="Password" value={pw}
//                  onChange={(e) => setPw(e.target.value)} />

const Wrap = styled.div`
  position: relative;
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition:
    background 150ms ease,
    color 150ms ease;

  &:hover,
  &:focus-visible {
    color: ${brand_orange_deep};
    background: rgba(216, 89, 7, 0.08);
  }

  &:focus-visible {
    outline: 2px solid ${brand_orange_deep};
    outline-offset: 2px;
  }

  [data-theme="dark"] & {
    color: ${dark.text_muted};

    &:hover,
    &:focus-visible {
      color: #fdba74;
    }
  }

  & svg {
    width: 20px;
    height: 20px;
  }
`;

const EyeOpenIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.77 19.77 0 0 1 5.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a19.77 19.77 0 0 1-2.17 3.19M1 1l22 22" />
    <path d="M9.5 9.5a3 3 0 0 0 4 4" />
  </svg>
);

export default function PasswordField({
  id,
  label,
  required,
  hint,
  error,
  value,
  onChange,
  onBlur,
  placeholder,
  autoComplete = "new-password",
}) {
  const [visible, setVisible] = React.useState(false);

  return (
    <FormField
      id={id}
      label={label}
      required={required}
      hint={hint}
      error={error}
    >
      <Wrap>
        <Control
          type={visible ? "text" : "password"}
          value={value ?? ""}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          autoComplete={autoComplete}
          $trailingPad
        />
        <ToggleButton
          type="button"
          aria-label={visible ? "Sembunyikan password" : "Tampilkan password"}
          aria-pressed={visible}
          onClick={() => setVisible((v) => !v)}
          tabIndex={0}
        >
          {visible ? <EyeOffIcon /> : <EyeOpenIcon />}
        </ToggleButton>
      </Wrap>
    </FormField>
  );
}
